import React from "react";

/* ================================================================
   Emotions & Concepts — 8 animated templates
   Idea, thumbs up, doubt, happy, love, celebration, thinking, surprise
   Flat 2D style with expressive animations
   ================================================================ */

// ── 1. Idea Lightbulb ────────────────────────────────────────────────
export const IdeaLightbulb: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const glowR = 60 + animationProgress * 30;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Glow */}
      <circle cx="400" cy="200" r={glowR} fill="#fbbf24" opacity="0.15">
        <animate attributeName="r" values="50;90;50" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.1;0.25;0.1" dur="2s" repeatCount="indefinite" />
      </circle>
      {/* Bulb */}
      <ellipse cx="400" cy="190" rx="55" ry="65" fill="#fef3c7" stroke="#f59e0b" strokeWidth="3" />
      <path d="M370,245 Q370,280 380,295 L420,295 Q430,280 430,245" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
      {/* Filament */}
      <path d="M385,180 Q395,160 400,180 Q405,160 415,180" stroke="#f59e0b" strokeWidth="3" fill="none">
        <animate attributeName="stroke" values="#f59e0b;#fbbf24;#f59e0b" dur="1s" repeatCount="indefinite" />
      </path>
      {/* Base */}
      <rect x="382" y="295" width="36" height="8" rx="2" fill="#9ca3af" />
      <rect x="385" y="303" width="30" height="6" rx="2" fill="#6b7280" />
      <rect x="388" y="309" width="24" height="6" rx="2" fill="#9ca3af" />
      {/* Rays */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
        const r1 = 80;
        const r2 = 100 + animationProgress * 15;
        return (
          <line key={i} x1={400 + Math.cos(angle) * r1} y1={190 + Math.sin(angle) * r1} x2={400 + Math.cos(angle) * r2} y2={190 + Math.sin(angle) * r2} stroke="#fbbf24" strokeWidth="3" strokeLinecap="round">
            <animate attributeName="opacity" values="0.3;1;0.3" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      <text x="400" y="380" textAnchor="middle" fill="#fbbf24" fontSize="22" fontWeight="bold">Idea!</text>
      <text x="400" y="410" textAnchor="middle" fill="#94a3b8" fontSize="13">A moment of inspiration</text>
    </svg>
  );
};

// ── 2. Thumbs Up ─────────────────────────────────────────────────────
export const ThumbsUp: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Circle bg */}
      <circle cx="400" cy="230" r={100 + animationProgress * 20} fill="#10b981" opacity="0.12">
        <animate attributeName="r" values="90;120;90" dur="2s" repeatCount="indefinite" />
      </circle>
      {/* Thumb */}
      <g transform="translate(400,230) scale(1.8)">
        <animateTransform attributeName="transform" type="translate" values="400,232;400,225;400,232" dur="1.5s" repeatCount="indefinite" additive="replace" />
        {/* Fist */}
        <rect x="-20" y="0" width="40" height="35" rx="8" fill="#fbbf24" />
        {/* Thumb up */}
        <rect x="-8" y="-45" width="18" height="50" rx="9" fill="#fbbf24" />
        {/* Fingers */}
        <rect x="-20" y="2" width="40" height="7" rx="3" fill="#e5a50a" />
        <rect x="-20" y="12" width="40" height="7" rx="3" fill="#e5a50a" />
        <rect x="-20" y="22" width="40" height="7" rx="3" fill="#e5a50a" />
      </g>
      {/* Sparkles */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        const r = 130;
        return (
          <text key={i} x={400 + Math.cos(angle) * r} y={220 + Math.sin(angle) * r} fontSize="18" textAnchor="middle">
            <animate attributeName="opacity" values="0;1;0" dur={`${1 + i * 0.3}s`} repeatCount="indefinite" />
            {i % 2 === 0 ? "✨" : "⭐"}
          </text>
        );
      })}
      <text x="400" y="400" textAnchor="middle" fill="#10b981" fontSize="22" fontWeight="bold">Great Job!</text>
    </svg>
  );
};

// ── 3. Question / Doubt ──────────────────────────────────────────────
export const QuestionDoubt: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const wobble = Math.sin(phase * 1.2) * 5;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Thinking head */}
      <circle cx="400" cy="280" r="60" fill="#fbbf24" />
      {/* Eyes - confused */}
      <g>
        <ellipse cx="380" cy="270" rx="6" ry="7" fill="#1e293b" />
        <ellipse cx="420" cy="270" rx="6" ry="7" fill="#1e293b" />
        {/* Eyebrows raised unevenly */}
        <line x1="372" y1="255" x2="388" y2="250" stroke="#1e293b" strokeWidth="3" strokeLinecap="round" />
        <line x1="412" y1="250" x2="428" y2="255" stroke="#1e293b" strokeWidth="3" strokeLinecap="round">
          <animateTransform attributeName="transform" type="translate" values="0,0;0,-3;0,0" dur="2s" repeatCount="indefinite" />
        </line>
      </g>
      {/* Squiggly mouth */}
      <path d="M385,300 Q393,295 400,300 Q407,305 415,300" stroke="#1e293b" strokeWidth="2.5" fill="none" />
      {/* Question marks */}
      <text x={370 + wobble} y="180" fontSize="40" fill="#6366f1" fontWeight="bold" opacity="0.8">
        ?
        <animateTransform attributeName="transform" type="translate" values="0,0;-5,-5;0,0" dur="2s" repeatCount="indefinite" />
      </text>
      <text x={430 - wobble} y="160" fontSize="28" fill="#8b5cf6" fontWeight="bold" opacity="0.6">
        ?
        <animateTransform attributeName="transform" type="translate" values="0,0;5,-8;0,0" dur="2.5s" repeatCount="indefinite" />
      </text>
      <text x={450} y="200" fontSize="20" fill="#a78bfa" fontWeight="bold" opacity="0.4">
        ?
        <animateTransform attributeName="transform" type="translate" values="0,0;3,-4;0,0" dur="3s" repeatCount="indefinite" />
      </text>
      {/* Scratching head */}
      <path d="M440,240 Q470,220 460,200" stroke="#fbbf24" strokeWidth="8" fill="none" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" values="0,440,240;5,440,240;0,440,240;-3,440,240;0,440,240" dur="2s" repeatCount="indefinite" />
      </path>
      <text x="400" y="410" textAnchor="middle" fill="#a78bfa" fontSize="20" fontWeight="bold">Hmm... I wonder?</text>
    </svg>
  );
};

// ── 4. Happy Smiley ──────────────────────────────────────────────────
export const HappySmiley: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const bounce = Math.sin(phase * 1.5) * 8;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Sun rays */}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => {
        const angle = (i / 12) * Math.PI * 2;
        return (
          <line key={i} x1={400 + Math.cos(angle) * 100} y1={230 + Math.sin(angle) * 100} x2={400 + Math.cos(angle) * 130} y2={230 + Math.sin(angle) * 130} stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" opacity="0.4">
            <animate attributeName="opacity" values="0.2;0.6;0.2" dur={`${1.5 + i * 0.1}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Face */}
      <circle cx="400" cy={230 + bounce} r="80" fill="#fbbf24" stroke="#f59e0b" strokeWidth="3">
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-8;0,0" dur="1.5s" repeatCount="indefinite" />
      </circle>
      {/* Eyes */}
      <ellipse cx="370" cy={215 + bounce} rx="8" ry="10" fill="#1e293b" />
      <ellipse cx="430" cy={215 + bounce} rx="8" ry="10" fill="#1e293b" />
      <ellipse cx="373" cy={210 + bounce} rx="3" ry="3" fill="#fff" />
      <ellipse cx="433" cy={210 + bounce} rx="3" ry="3" fill="#fff" />
      {/* Big smile */}
      <path d={`M355,${250 + bounce} Q400,${290 + bounce} 445,${250 + bounce}`} stroke="#1e293b" strokeWidth="4" fill="none" />
      {/* Rosy cheeks */}
      <circle cx="348" cy={245 + bounce} r="12" fill="#f472b6" opacity="0.3" />
      <circle cx="452" cy={245 + bounce} r="12" fill="#f472b6" opacity="0.3" />
      <text x="400" y="400" textAnchor="middle" fill="#fbbf24" fontSize="24" fontWeight="bold">Happy!</text>
    </svg>
  );
};

// ── 5. Love Hearts ───────────────────────────────────────────────────
export const LoveHearts: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const hearts = [
    { x: 400, y: 200, size: 1.5, color: "#ef4444", delay: 0 },
    { x: 300, y: 280, size: 0.8, color: "#f472b6", delay: 0.5 },
    { x: 500, y: 270, size: 0.9, color: "#ec4899", delay: 1 },
    { x: 350, y: 150, size: 0.6, color: "#fb7185", delay: 1.5 },
    { x: 460, y: 160, size: 0.7, color: "#f43f5e", delay: 0.8 },
    { x: 250, y: 200, size: 0.5, color: "#fda4af", delay: 2 },
    { x: 550, y: 200, size: 0.55, color: "#f9a8d4", delay: 1.2 },
  ];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {hearts.map((h, i) => {
        const beat = 1 + Math.sin((phase + h.delay) * 2.5) * 0.12;
        const floatY = Math.sin((phase + h.delay) * 0.8) * 8;
        return (
          <g key={i} transform={`translate(${h.x},${h.y + floatY}) scale(${h.size * beat})`}>
            <path d="M0,-20 C-20,-45 -50,-15 0,25 C50,-15 20,-45 0,-20 Z" fill={h.color} opacity="0.85" />
          </g>
        );
      })}
      {/* Sparkle particles */}
      {[0, 1, 2, 3, 4].map((i) => (
        <circle key={`s${i}`} cx={300 + i * 50} cy={350} r="2" fill="#fbbf24">
          <animate attributeName="cy" values={`350;${300 - i * 20};350`} dur={`${2 + i * 0.4}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;1;0" dur={`${2 + i * 0.4}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <text x="400" y="420" textAnchor="middle" fill="#ec4899" fontSize="22" fontWeight="bold">Love</text>
    </svg>
  );
};

// ── 6. Celebration / Confetti ────────────────────────────────────────
export const CelebrationConfetti: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const confettiColors = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Trophy / Star center */}
      <g transform="translate(400,220)">
        <polygon points="0,-60 15,-20 60,-15 25,15 35,60 0,35 -35,60 -25,15 -60,-15 -15,-20" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2">
          <animateTransform attributeName="transform" type="scale" values="0.9;1.1;0.9" dur="2s" repeatCount="indefinite" />
        </polygon>
      </g>
      {/* Confetti falling */}
      {Array.from({ length: 30 }).map((_, i) => {
        const x = 50 + (i * 73) % 700;
        const delay = (i * 0.3) % 3;
        const color = confettiColors[i % confettiColors.length];
        const w = 4 + (i % 3) * 3;
        const h = 6 + (i % 4) * 2;
        const startY = -20 - (i % 5) * 30;
        return (
          <rect key={i} x={x} y={startY} width={w} height={h} rx="1" fill={color} opacity="0.8">
            <animate attributeName="y" values={`${startY};${480}`} dur={`${2.5 + delay}s`} repeatCount="indefinite" />
            <animateTransform attributeName="transform" type="rotate" values={`0,${x},${startY};${360},${x},400`} dur={`${2.5 + delay}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.9;0.6;0.3" dur={`${2.5 + delay}s`} repeatCount="indefinite" />
          </rect>
        );
      })}
      <text x="400" y="380" textAnchor="middle" fill="#fbbf24" fontSize="24" fontWeight="bold">🎉 Celebration!</text>
    </svg>
  );
};

// ── 7. Thinking Cloud ────────────────────────────────────────────────
export const ThinkingCloud: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const drift = Math.sin(phase * 0.5) * 5;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Person silhouette */}
      <circle cx="300" cy="340" r="35" fill="#6366f1" />
      <rect x="280" y="375" width="40" height="55" rx="10" fill="#4f46e5" />
      <circle cx="300" cy="330" r="4" fill="#fff" />
      <line x1="300" y1="350" x2="310" y2="355" stroke="#1e293b" strokeWidth="2" />
      {/* Thought bubbles */}
      <circle cx="350" cy="300" r="8" fill="#e0e7ff" opacity="0.6">
        <animate attributeName="r" values="6;10;6" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx="380" cy="270" r="12" fill="#e0e7ff" opacity="0.7">
        <animate attributeName="r" values="10;14;10" dur="3s" repeatCount="indefinite" />
      </circle>
      {/* Main thought cloud */}
      <g transform={`translate(${drift},${drift * 0.5})`}>
        <ellipse cx="500" cy="200" rx="120" ry="70" fill="#e0e7ff" />
        <ellipse cx="430" cy="210" rx="40" ry="35" fill="#e0e7ff" />
        <ellipse cx="570" cy="215" rx="35" ry="30" fill="#e0e7ff" />
        <ellipse cx="500" cy="155" rx="50" ry="30" fill="#e0e7ff" />
        {/* Content inside cloud */}
        <text x="500" y="190" textAnchor="middle" fill="#4f46e5" fontSize="14">💡 What if...</text>
        <text x="500" y="215" textAnchor="middle" fill="#6366f1" fontSize="12">🔑 The key is...</text>
        {/* Spinning gear in thought */}
        <circle cx="470" cy="200" r="3" fill="#818cf8">
          <animateTransform attributeName="transform" type="rotate" values="0,470,200;360,470,200" dur="4s" repeatCount="indefinite" />
        </circle>
      </g>
      <text x="400" y="460" textAnchor="middle" fill="#a5b4fc" fontSize="18" fontWeight="bold">Deep Thinking</text>
    </svg>
  );
};

// ── 8. Surprise / Wow ────────────────────────────────────────────────
export const SurpriseWow: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Explosion lines */}
      {Array.from({ length: 16 }).map((_, i) => {
        const angle = (i / 16) * Math.PI * 2;
        const r1 = 100 + animationProgress * 20;
        const r2 = 140 + animationProgress * 30;
        return (
          <line key={i} x1={400 + Math.cos(angle) * r1} y1={230 + Math.sin(angle) * r1} x2={400 + Math.cos(angle) * r2} y2={230 + Math.sin(angle) * r2} stroke="#f59e0b" strokeWidth={i % 2 === 0 ? 3 : 2} strokeLinecap="round">
            <animate attributeName="opacity" values="0;1;0" dur={`${0.8 + (i % 4) * 0.2}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Face */}
      <circle cx="400" cy="230" r="70" fill="#fbbf24" stroke="#f59e0b" strokeWidth="3">
        <animateTransform attributeName="transform" type="scale" values="1;1.05;1" dur="0.5s" repeatCount="indefinite" />
      </circle>
      {/* Wide eyes */}
      <ellipse cx="375" cy="215" rx="12" ry="16" fill="#fff" stroke="#1e293b" strokeWidth="2" />
      <circle cx="375" cy="215" r="6" fill="#1e293b" />
      <ellipse cx="425" cy="215" rx="12" ry="16" fill="#fff" stroke="#1e293b" strokeWidth="2" />
      <circle cx="425" cy="215" r="6" fill="#1e293b" />
      {/* O mouth */}
      <ellipse cx="400" cy="260" rx="15" ry="20" fill="#1e293b" />
      {/* Eyebrows raised high */}
      <path d="M358,192 Q368,180 382,190" stroke="#1e293b" strokeWidth="3" fill="none" />
      <path d="M418,190 Q432,180 442,192" stroke="#1e293b" strokeWidth="3" fill="none" />
      {/* Exclamation */}
      <text x="400" y="100" textAnchor="middle" fill="#ef4444" fontSize="48" fontWeight="bold">
        !
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-10;0,0" dur="0.6s" repeatCount="indefinite" />
      </text>
      <text x="400" y="400" textAnchor="middle" fill="#f59e0b" fontSize="24" fontWeight="bold">Wow!</text>
    </svg>
  );
};
